import { Component, OnDestroy, OnInit, signal, computed, inject, TrackByFunction } from '@angular/core';
import {
  DraggableContainerComponent,
  QaiButtonComponent,
  QaiIconComponent,
  QaiSimulationCardComponent,
  QaiLoadingComponent,
  DraggableItem,
  QaiSimulationSettingLabels,
  QaiSimulationCardAttemptsLabels,
  QaiSimulationSettingOption,
} from '@qai/ui';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, concatMap, defer, EMPTY, finalize, Observable, Subject, Subscription, take, tap } from 'rxjs';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { ActivatedRoute } from '@angular/router';
import { AddSimulationModalComponent } from '../../components/modals/add-simulation-to-assignment/add-simulation-to-assignment.component';
import { ISimulationByModuleAssignment, ISimulationsInModuleAssignment } from 'src/app/interfaces/self-serve/assignment-manager/responses/isimulation-by-module-assignment';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { ISimulationsInAssignment } from 'src/app/interfaces/self-serve/assignment-manager/isimulation-in-assignment';
import { ISimulationPayload, IUpdateSimulationsLink } from 'src/app/interfaces/self-serve/assignment-manager/requests/isimulation-in-assignment';
import { AssignmentReactiveFormService } from '../../services/assignment-reactive-form.service';
import { FormGroup } from '@angular/forms';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { QuestionStrategyEnum } from 'src/app/utils/enums/self-serve/question-strategy.enum';
import { ComplianceScoreImpactEnum } from 'src/app/utils/enums/self-serve/compliance-score-impact.enum';
import {
  buildComplianceScoreImpactLabels,
  buildComplianceScoreImpactOptions,
  resolveComplianceScoreImpact,
} from 'src/app/utils/constants/self-serve/compliance-score-impact.constants';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

type SavedRandomization = Pick<ISimulationPayload, 'questionStrategy' | 'randomQuestionPool' | 'randomQuestionCount'>;

interface RandomizationFormState {
  enabled: boolean;
  questionStrategy: QuestionStrategyEnum | null;
  randomQuestionPool: string;
  randomQuestionCount: number | null;
  poolError: string | null;
  countError: string | null;
}

@Component({
  selector: 'tab-simulation-details',
  standalone: true,
  templateUrl: './simulations-content.component.html',
  styleUrls: ['./simulations-content.component.scss'],
  imports: [
    DraggableContainerComponent,
    QaiSimulationCardComponent,
    QaiButtonComponent,
    QaiIconComponent,
    TabsInnerWrapComponent,
    AddSimulationModalComponent,
    QaiLoadingComponent,
  ],
})
export class SimulationDetailsComponent implements OnInit, OnDestroy {
  readonly isLoading = signal(true);
  readonly simulations = signal<ISimulationsInAssignment[]>([]);
  readonly addSimulationsToAssignmentModalOpen = signal(false);
  readonly isCompleted = signal(false);
  readonly showAttemptsAllowed = signal(true);
  readonly randomizationState = signal<Record<string, RandomizationFormState>>({});
  readonly complianceScoreImpactEnabled = signal(false);
  readonly complianceScoreOptions = signal<QaiSimulationSettingOption[]>([]);
  readonly complianceScoreLabels = signal<QaiSimulationSettingLabels | undefined>(undefined);
  readonly attemptsLabels = signal<QaiSimulationCardAttemptsLabels | undefined>(undefined);
  readonly removeLabel = signal<string | undefined>(undefined);
  // Same display names as the learner assignment page ("Role Play", "Knowledge Check")
  private readonly callTypeLabels = signal<Record<string, string>>({});
  // Last values the backend accepted per simulation: a failed save reverts to them and a reorder resends them
  private readonly savedComplianceScoreImpact = new Map<string, ComplianceScoreImpactEnum | null | undefined>();
  private readonly savedRandomization = new Map<string, SavedRandomization>();
  // Saves run one at a time in the order they were made, and build their request when their turn comes
  private readonly saveQueue = new Subject<() => Observable<unknown>>();
  private readonly saveQueueSubscription = this.saveQueue.pipe(concatMap(save => defer(save).pipe(catchError(() => EMPTY)))).subscribe();

  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly amplitudeService = inject(AmplitudeService);

  moduleAssignmentUuid = '';
  moduleUuid = '';
  selectedCard: ISimulationsInModuleAssignment = {} as ISimulationsInModuleAssignment;

  private assignmentTypeSubscription?: Subscription;

  protected readonly labels = {
    noSimulations: 'No simulations found',
    attemptsAllowed: 'Attempts allowed',
    randomizedQuestions: 'Randomized questions',
    questionsPerUser: 'Questions per user',
    randomizationPool: 'Randomization pool',
    attemptsSubLabel: 'Number of attempts per user',
    countSubLabel: 'Number of questions each user will receive',
    poolPlaceholder: 'Enter question UUIDs',
    selectPlaceholder: 'Select',
    addSimulation: 'Add simulation to assignment',
    removeSimulationTitle: 'Remove simulation from assignment',
    invalidPoolError: 'One or more question IDs are invalid',
    duplicatePoolError: 'Duplicate question IDs are not allowed',
    countPositiveError: 'Question count must be a positive integer.',
    countExceedsPoolError: 'Exceeds available question pool',
    attemptsUnlimited: 'Unlimited',
    strategyNo: 'No',
    strategyYesPerUser: 'Yes, per user',
    strategyYesPerSession: 'Yes, per session',
  } as const;

  readonly attemptsOptions = [
    { value: 'unlimited', label: this.labels.attemptsUnlimited },
    ...Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
  ];
  readonly strategyOptions = [
    { value: 'NO', label: this.labels.strategyNo },
    { value: QuestionStrategyEnum.RANDOM_FIXED, label: this.labels.strategyYesPerUser },
    { value: QuestionStrategyEnum.RANDOM_EVERY_TIME, label: this.labels.strategyYesPerSession },
  ];
  readonly countSelectOptionsMap = computed<Record<string, { value: string; label: string }[]>>(() =>
    Object.fromEntries(
      this.simulations().map(sim => {
        const max = (sim.totalQuestionCount ?? 0) > 0 ? sim.totalQuestionCount! : 20;
        return [sim.callUuid, Array.from({ length: max }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))];
      })
    )
  );

  getCountSelectOptions(callUuid: string): { value: string; label: string }[] {
    return this.countSelectOptionsMap()[callUuid]
      ?? Array.from({ length: 20 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  }

  protected readonly randomizationLabels = {
    randomizedQuestions: this.labels.randomizedQuestions,
    questionsPerUser: this.labels.questionsPerUser,
    countSubLabel: this.labels.countSubLabel,
    randomizationPool: this.labels.randomizationPool,
    poolPlaceholder: this.labels.poolPlaceholder,
  };

  constructor(
    private assignmentsService: AssignmentsService,
    private assignmentReactiveFormService: AssignmentReactiveFormService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.moduleAssignmentUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.isCompleted.set(this.assignmentReactiveFormService.isCompleted(this.moduleAssignmentUuid));
    this.loadModuleUuid();
    this.fetchSimulations();
    this.subscribeToAssignmentTypeChanges();
    this.loadComplianceScoreImpact();
  }

  ngOnDestroy(): void {
    this.assignmentTypeSubscription?.unsubscribe();
    this.saveQueue.complete();
  }

  private subscribeToAssignmentTypeChanges(): void {
    this.assignmentReactiveFormService.getForm(this.moduleAssignmentUuid).pipe(take(1)).subscribe((form) => {
      const detailsGroup = form.get('details') as FormGroup;
      const assignmentTypeControl = detailsGroup?.get('moduleAssignmentType');

      if (assignmentTypeControl) {
        this.updateShowAttemptsAllowed(assignmentTypeControl.value);

        this.assignmentTypeSubscription?.unsubscribe();
        this.assignmentTypeSubscription = assignmentTypeControl.valueChanges.subscribe((assignmentType: AssignmentTypeEnum) => {
          this.updateShowAttemptsAllowed(assignmentType);
        });
      }
    });
  }

  private updateShowAttemptsAllowed(assignmentType: AssignmentTypeEnum): void {
    this.showAttemptsAllowed.set(assignmentType !== AssignmentTypeEnum.PERSONALIZED_PRACTICE);
  }

  private async loadComplianceScoreImpact(): Promise<void> {
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      const translate = (key: string) => this.translocoService.translate(key);
      this.complianceScoreOptions.set(buildComplianceScoreImpactOptions(translate));
      this.complianceScoreLabels.set(buildComplianceScoreImpactLabels(translate));
      this.attemptsLabels.set({
        label: translate('simulationBuilder.ATTEMPTS_ALLOWED_LABEL'),
        description: translate('simulationBuilder.ATTEMPTS_ALLOWED_DESCRIPTION'),
      });
      this.removeLabel.set(translate('simulationBuilder.REMOVE_SIMULATION_FROM_ASSIGNMENT'));
      this.callTypeLabels.set({
        CONVERSATIONAL: translate('simulationBuilder.SIMULATION_TYPE_ROLE_PLAY_TITLE'),
        GUIDED: translate('simulationBuilder.SIMULATION_TYPE_KNOWLEDGE_CHECK_TITLE'),
      });
    });
    this.complianceScoreImpactEnabled.set(
      await this.featureFlagsService.isFlagEnabled(AppConstants.COMPLIANCE_SCORE_IMPACT)
    );
  }

  getCallTypeLabel(callType: string): string {
    return this.callTypeLabels()[callType] ?? callType;
  }

  showComplianceScore(item: ISimulationsInAssignment): boolean {
    return this.complianceScoreImpactEnabled() && item.hasCompliance === true;
  }

  getComplianceScoreImpact(item: ISimulationsInAssignment): ComplianceScoreImpactEnum {
    return resolveComplianceScoreImpact(item.complianceScoreImpact);
  }

  // Keeps each card when one simulation is replaced, so the list is not rebuilt
  readonly trackByCallUuid: TrackByFunction<DraggableItem> = (_, item) => item['callUuid'];

  onComplianceScoreImpactChange(item: ISimulationsInAssignment, value: string): void {
    const complianceScoreImpact = value as ComplianceScoreImpactEnum;
    // Show the new value right away so the radio does not jump back while saving
    this.setComplianceScoreImpact(item.callUuid, complianceScoreImpact);
    this.saveQueue.next(() => this.saveComplianceScoreImpact(item.callUuid));
  }

  // Saves the value the card shows when this save's turn comes, so quick changes send at most the newest one
  private saveComplianceScoreImpact(callUuid: string): Observable<unknown> {
    const sims = this.simulations();
    const index = sims.findIndex(sim => sim.callUuid === callUuid);
    const sim = sims[index];
    const complianceScoreImpact = sim?.complianceScoreImpact;
    // Removed meanwhile, or the shown value is already saved
    if (!complianceScoreImpact || complianceScoreImpact === this.savedComplianceScoreImpact.get(callUuid)) return EMPTY;
    // One simulation and one update flag per request (backend rule)
    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: false,
      updateComplianceScoreImpact: true,
      simulations: [{
        callUuid,
        callOrder: index + 1,
        numberOfAttemptsAllowed: typeof sim?.numberOfAttemptsAllowed === 'number' ? sim.numberOfAttemptsAllowed : null,
        complianceScoreImpact,
      }],
    };

    return this.assignmentsService.updateSimulationsLink(payload).pipe(
      tap({
        next: () => {
          this.savedComplianceScoreImpact.set(callUuid, complianceScoreImpact);
          this.amplitudeService.trackEvent(AmplitudeTrackingEvents.COMPLIANCE_SCORE_IMPACT_SET, {
            assignment_uuid: this.moduleAssignmentUuid,
            call_uuid: callUuid,
            compliance_feedback: complianceScoreImpact,
          });
        },
        error: (err) => {
          // A newer choice is saved by its own turn, so only revert when the failed value is still shown
          const shown = this.simulations().find(sim => sim.callUuid === callUuid)?.complianceScoreImpact;
          if (shown === complianceScoreImpact) {
            this.setComplianceScoreImpact(callUuid, this.savedComplianceScoreImpact.get(callUuid));
          }
          console.error('Error updating compliance score impact', err);
        },
      })
    );
  }

  private setComplianceScoreImpact(callUuid: string, complianceScoreImpact: ComplianceScoreImpactEnum | null | undefined): void {
    this.simulations.update(sims =>
      sims.map(sim => (sim.callUuid === callUuid ? { ...sim, complianceScoreImpact } : sim))
    );
  }

  private fetchSimulations(): void {
    if (!this.moduleAssignmentUuid) {
      console.warn('No moduleAssignmentUuid provided.');
      this.simulations.set([]);
      this.isLoading.set(false);
      return;
    }

    this.assignmentsService
      .getSimulationsByModuleAssignmentUuid(this.moduleAssignmentUuid)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((res: ISimulationByModuleAssignment) => {
        if (res?.simulations) {
          // A choice still waiting in the queue stays on the card after the reload
          const unsaved = new Map(this.simulations()
            .filter(sim => sim.complianceScoreImpact !== this.savedComplianceScoreImpact.get(sim.callUuid))
            .map(sim => [sim.callUuid, sim.complianceScoreImpact]));
          this.savedComplianceScoreImpact.clear();
          this.savedRandomization.clear();
          res.simulations.forEach(sim => {
            this.savedComplianceScoreImpact.set(sim.callUuid, sim.complianceScoreImpact);
            this.savedRandomization.set(sim.callUuid, {
              questionStrategy: sim.questionStrategy,
              randomQuestionPool: sim.randomQuestionPool,
              randomQuestionCount: sim.randomQuestionCount,
            });
          });
          this.simulations.set(
            res.simulations.map(sim => ({
              ...sim,
              numberOfAttemptsAllowed: sim.numberOfAttemptsAllowed === null ? 'unlimited' : sim.numberOfAttemptsAllowed,
              complianceScoreImpact: unsaved.get(sim.callUuid) ?? sim.complianceScoreImpact,
            }))
          );
          this.showAttemptsAllowed.set(res.moduleAssignmentType !== AssignmentTypeEnum.PERSONALIZED_PRACTICE);

          const initialState: Record<string, RandomizationFormState> = {};
          res.simulations.forEach(sim => {
            const isEnabled = !!sim.questionStrategy && sim.questionStrategy !== QuestionStrategyEnum.STANDARD;
            initialState[sim.callUuid] = {
              enabled: isEnabled,
              questionStrategy: isEnabled ? (sim.questionStrategy as QuestionStrategyEnum) : null,
              randomQuestionPool: sim.randomQuestionPool ?? '',
              randomQuestionCount: sim.randomQuestionCount ?? null,
              poolError: null,
              countError: null,
            };
          });
          this.randomizationState.set(initialState);
        } else {
          this.simulations.set([]);
        }

        this.assignmentReactiveFormService.getForm(this.moduleAssignmentUuid).pipe(take(1)).subscribe((form) => {
          const detailsGroup = form.get('details') as FormGroup;
          if (detailsGroup.get('simulationsCount')) {
            this.assignmentReactiveFormService.patchForm(this.moduleAssignmentUuid, 'details', {
              simulationsCount: this.simulations().length,
            });
          }
        });
      });
  }

  onSimulationsChange(updatedSimulations: DraggableItem[]): void {
    if (!this.moduleAssignmentUuid) {
      console.warn('moduleAssignmentUuid not set. Skipping API call.');
      return;
    }

    // The reorder reads the list when its turn comes, so it never brings back a simulation removed before it
    this.simulations.set([...updatedSimulations] as ISimulationsInAssignment[]);
    this.saveQueue.next(() => this.saveOrder());
  }

  private saveOrder(): Observable<unknown> {
    // Reorder recreates every link, so send each simulation's saved settings or they reset.
    // Saved values, not the form, so an unsaved or invalid value is never stored by a reorder
    const simulationsPayload: ISimulationPayload[] = this.simulations().map((item, index) => {
      const random = this.savedRandomization.get(item.callUuid);
      return {
        callUuid: item.callUuid,
        numberOfAttemptsAllowed: typeof item.numberOfAttemptsAllowed === 'number' ? item.numberOfAttemptsAllowed : null,
        callOrder: index + 1,
        questionStrategy: random?.questionStrategy ?? QuestionStrategyEnum.STANDARD,
        randomQuestionCount: random?.randomQuestionCount ?? null,
        randomQuestionPool: random?.randomQuestionPool ?? null,
        complianceScoreImpact: this.savedComplianceScoreImpact.get(item.callUuid) ?? null,
      };
    });

    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: false,
      simulations: simulationsPayload,
    };

    return this.assignmentsService.updateSimulationsLink(payload).pipe(tap({ error: (err) => console.error('Error updating simulations order:', err) }));
  }

  removeLinkClick(item: ISimulationsInAssignment): void {
    if (!this.moduleAssignmentUuid) {
      console.warn('moduleAssignmentUuid not set. Skipping API call.');
      return;
    }

    // Queued so a reorder waiting before it can't recreate the removed link
    this.saveQueue.next(() => this.assignmentsService.removeSimulationFromAssignment(this.moduleAssignmentUuid, item.callUuid).pipe(tap({
      next: () => {
        this.simulations.update(sims => sims.filter(sim => sim.callUuid !== item.callUuid));
        this.fetchSimulations();
      },
      error: (err) => console.error('Failed to remove simulation:', err),
    })));
  }

  setSelectedCard(item: ISimulationsInModuleAssignment): void {
    this.selectedCard = item;
  }

  onAttemptsSelected(value: number | string): void {
    if (!this.selectedCard) return;

    const updatedCard: ISimulationsInModuleAssignment = {
      ...this.selectedCard,
      numberOfAttemptsAllowed: typeof value === 'number' ? value : null,
    };

    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: true,
      simulations: [updatedCard],
    };

    this.saveQueue.next(() => this.assignmentsService.updateSimulationsLink(payload).pipe(tap({
      next: () => {
        this.simulations.update(sims =>
          sims.map(sim =>
            sim.callUuid === updatedCard.callUuid
              ? { ...sim, numberOfAttemptsAllowed: updatedCard.numberOfAttemptsAllowed === null ? 'unlimited' : updatedCard.numberOfAttemptsAllowed }
              : sim
          )
        );
        this.selectedCard = {} as ISimulationsInModuleAssignment;
      },
      error: (err) => console.error('Error updating simulation attempts', err),
    })));
  }

  openAddSimulationModal(): void {
    this.loadModuleUuid();
    this.addSimulationsToAssignmentModalOpen.set(true);
  }

  private loadModuleUuid(): void {
    this.assignmentReactiveFormService
      .getForm(this.moduleAssignmentUuid)
      .pipe(take(1))
      .subscribe((form) => {
        const detailsGroup = form.get('details') as FormGroup;
        this.moduleUuid = detailsGroup?.get('moduleUuid')?.value || '';
      });
  }

  closeAssignmentModal(): void {
    this.addSimulationsToAssignmentModalOpen.set(false);
  }

  closeModalAndReloadSimulations(): void {
    this.closeAssignmentModal();
    this.fetchSimulations();
  }

  // --- Randomization helpers ---

  getRandomState(callUuid: string): RandomizationFormState {
    return this.randomizationState()[callUuid] ?? {
      enabled: false,
      questionStrategy: null,
      randomQuestionPool: '',
      randomQuestionCount: null,
      poolError: null,
      countError: null,
    };
  }

  onStrategyChange(item: ISimulationsInModuleAssignment, value: string): void {
    if (value === 'NO') {
      this.randomizationState.update(s => ({
        ...s,
        [item.callUuid]: {
          enabled: false,
          questionStrategy: null,
          randomQuestionPool: '',
          randomQuestionCount: null,
          poolError: null,
          countError: null,
        },
      }));
      this.saveRandomizationStrategy(item, QuestionStrategyEnum.STANDARD, null, null);
    } else {
      const strategy = value as QuestionStrategyEnum;
      const currentState = this.randomizationState()[item.callUuid];

      if (!currentState?.enabled) {
        this.randomizationState.update(s => ({
          ...s,
          [item.callUuid]: {
            enabled: true,
            questionStrategy: strategy,
            randomQuestionPool: '',
            randomQuestionCount: null,
            poolError: null,
            countError: null,
          },
        }));
      } else {
        this.updateRandomState(item.callUuid, { questionStrategy: strategy });
      }

      const newState = this.randomizationState()[item.callUuid];
      // An invalid typed pool isn't saved, so keep the saved pool and count with the new strategy
      const saved = this.savedRandomization.get(item.callUuid);
      const pool = newState.poolError ? (saved?.randomQuestionPool ?? null) : newState.randomQuestionPool || null;
      const count = newState.poolError ? (saved?.randomQuestionCount ?? null) : newState.randomQuestionCount;
      this.saveRandomizationStrategy(item, strategy, pool, count);
    }
  }

  onPoolBlur(item: ISimulationsInModuleAssignment, event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.updateRandomState(item.callUuid, { randomQuestionPool: value });

    if (!value) {
      this.updateRandomState(item.callUuid, { poolError: this.labels.invalidPoolError });
      return;
    }

    const uuids = value.split(',').map(u => u.trim()).filter(u => !!u);

    if (new Set(uuids).size !== uuids.length) {
      this.updateRandomState(item.callUuid, { poolError: this.labels.duplicatePoolError });
      return;
    }

    this.assignmentsService.validateQuestionUuids(item.callUuid, uuids).subscribe({
      next: (res) => {
        if (res.valid) {
          this.updateRandomState(item.callUuid, { poolError: null });
          const state = this.randomizationState()[item.callUuid];
          if (state.randomQuestionCount !== null) {
            this.validateCount(item.callUuid, uuids.length);
          }
          const updatedState = this.randomizationState()[item.callUuid];
          if (!updatedState.countError) {
            this.saveRandomizationStrategy(item, updatedState.questionStrategy ?? QuestionStrategyEnum.STANDARD, value, updatedState.randomQuestionCount);
          }
        } else {
          this.updateRandomState(item.callUuid, { poolError: this.labels.invalidPoolError });
        }
      },
      error: () => {
        this.updateRandomState(item.callUuid, { poolError: this.labels.invalidPoolError });
      },
    });
  }

  onCountChange(item: ISimulationsInModuleAssignment, raw: string): void {
    const count = raw ? parseInt(raw, 10) : null;
    this.updateRandomState(item.callUuid, { randomQuestionCount: count });

    if (count === null || isNaN(count) || count < 1) {
      this.updateRandomState(item.callUuid, { countError: this.labels.countPositiveError });
      return;
    }

    const state = this.randomizationState()[item.callUuid];
    if (state.poolError) {
      this.updateRandomState(item.callUuid, { countError: null });
      return;
    }

    const poolUuids = state.randomQuestionPool ? state.randomQuestionPool.split(',').filter(u => !!u.trim()) : [];
    this.validateCount(item.callUuid, poolUuids.length);

    const updatedState = this.randomizationState()[item.callUuid];
    if (!updatedState.countError) {
      this.saveRandomizationStrategy(item, updatedState.questionStrategy ?? QuestionStrategyEnum.STANDARD, updatedState.randomQuestionPool || null, count);
    }
  }

  private updateRandomState(callUuid: string, updates: Partial<RandomizationFormState>): void {
    this.randomizationState.update(s => ({
      ...s,
      [callUuid]: { ...s[callUuid], ...updates },
    }));
  }

  private validateCount(callUuid: string, poolSize: number): void {
    const state = this.randomizationState()[callUuid];
    const countError =
      state.randomQuestionCount !== null && poolSize > 0 && state.randomQuestionCount > poolSize
        ? this.labels.countExceedsPoolError
        : null;
    this.updateRandomState(callUuid, { countError });
  }

  private saveRandomizationStrategy(
    item: ISimulationsInModuleAssignment,
    questionStrategy: QuestionStrategyEnum,
    randomQuestionPool: string | null,
    randomQuestionCount: number | null,
  ): void {
    if (!this.moduleAssignmentUuid) return;

    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: false,
      updateQuestionStrategy: true,
      simulations: [{
        callUuid: item.callUuid,
        callOrder: (item.callOrder as number) ?? 0,
        numberOfAttemptsAllowed: typeof item.numberOfAttemptsAllowed === 'number' ? item.numberOfAttemptsAllowed : null,
        questionStrategy,
        randomQuestionPool,
        randomQuestionCount,
      }],
    };

    this.saveQueue.next(() => this.assignmentsService.updateSimulationsLink(payload).pipe(tap({
      next: () => this.savedRandomization.set(item.callUuid, { questionStrategy, randomQuestionPool, randomQuestionCount }),
      error: (err) => console.error('Error updating randomization strategy', err),
    })));
  }
}
